import React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { B300, B400, N30, N80, N900, N200 } from '@atlaskit/theme/colors';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { FooterLogo, TextInput } from '../components';
import { messages } from '../../messages';
import { useFirstImpressionsState } from '../../use-first-impressions-state';
import {
  SanitizedFormattedHtmlMessage,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import { useSignUpFormGlobalConfiguration } from '../../../../hook';

export function FirstImpressionsLoggedInView() {
  const [formSubmitError, setFormSubmitError] = useState(false);
  const stargateClient = useStargateClient();
  const { checkEmailPageUrl } = useSignUpFormGlobalConfiguration();
  const { userInfo } = useFirstImpressionsState();

  const LoggedInForm = () => (
    <Formik
      initialValues={{
        email: '',
        fullName: ''
      }}
      validationSchema={Yup.object({
        siteName: Yup.string().max(255, () => (
          <FormattedMessage {...messages.inputLabelFullNameLengthError} />
        )),
        email: Yup.string()
          .email(() => (
            <FormattedMessage {...messages.inputLabelWorkEmailError} />
          ))
          .required(() => <FormattedMessage {...messages.inputLabelRequired} />)
      })}
      onSubmit={async (values) => {
        setFormSubmitError(false);
        try {
          await stargateClient.userSignup({
            displayName: values.fullName,
            email: values.email,
            redirectTo: window.location.href,
            locale: 'en',
            webPlatform: 'magnolia'
          });
          window.location.assign(checkEmailPageUrl);
        } catch (error) {
          setFormSubmitError(true);
        }
      }}
    >
      {({ dirty, isSubmitting, isValid }) => (
        <Form>
          <TextInput
            testId="first-impressions-work-email-text-input"
            name="email"
            type="email"
            label={<FormattedMessage {...messages.inputLabelWorkEmail} />}
            disabled={true}
            value={userInfo?.email || ''}
          />

          <TextInput
            testId="first-impressions-site-name-text-input"
            name="siteName"
            type="url"
            label={<FormattedMessage {...messages.inputLabelSiteName} />}
          />

          <Eula>
            <SanitizedFormattedHtmlMessage {...messages.firstImpressionsEula} />
          </Eula>

          <SubmitButton
            type="submit"
            disabled={!dirty || isSubmitting || !isValid}
          >
            <FormattedMessage {...messages.submitButtonLabel} />
          </SubmitButton>
          {formSubmitError && (
            <FailedSubmitMessage>
              <FormattedMessage
                {...messages.firstImpressionsLoggedOutFormSubmitError}
              />
            </FailedSubmitMessage>
          )}
        </Form>
      )}
    </Formik>
  );

  return (
    <FirstImpressionsLoggedOutViewContainer>
      <FormHeaderContainer>
        <FormHeaderHeading>
          <FormattedMessage
            {...messages.firstImpressionsLoggedInFormHeader}
            values={{
              userName: userInfo?.firstName
            }}
          />
        </FormHeaderHeading>
      </FormHeaderContainer>

      <LoggedInForm />

      <NoCreditCardRequiredFooterMessage>
        <FormattedMessage {...messages.noCreditCardRequiredFooter} />
      </NoCreditCardRequiredFooterMessage>
      <FooterLogo />
    </FirstImpressionsLoggedOutViewContainer>
  );
}

const fontCharlieText =
  '"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
  'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

const FirstImpressionsLoggedOutViewContainer = styled.div`
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const FormHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const FormHeaderHeading = styled.div`
  color: ${N900};
  font-family: 'Charlie Display';
  font-size: 36px;
  font-weight: 500;
  line-height: 40px;
`;

const NoCreditCardRequiredFooterMessage = styled.p`
  font-family: Charlie Display;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 17.5px;
  letter-spacing: 1px;
  align-items: center;
  text-align: center;
  text-transform: uppercase;
  margin: 16px 0 40px 0;
`;

const SubmitButton = styled.button`
  font-family: ${fontCharlieText};
  font-weight: 500;
  font-size: 16px;
  outline: none !important;
  border: none;
  border-radius: 3px;
  color: white;
  background-color: ${B400};
  width: 100%;
  height: 36px;
  &:disabled {
    color: ${N80};
    background-color: ${N30};
    opacity: 0.6;
  }
`;

const Eula = styled.div`
  font-family: ${fontCharlieText};
  font-size: 12px;
  line-height: 16px;
  color: ${N200};
  vertical-align: middle;
  padding: 0;
  font-weight: 400;

  a {
    color: ${B400};
    cursor: pointer;
    text-decoration: none;
    &:hover {
      color: ${B300};
      text-decoration: underline;
    }
  }

  p {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 24px;
  }
`;

const FailedSubmitMessage = styled.div`
  color: #de350b;
  font-weight: 400;
  font-size: 12px;
  font-family: ${fontCharlieText};
`;
